<template>
	<article class="d-job-list-custom">
		<PageHero
			:title="header.title"
			:description="header.description"
			v-bind="{ breadcrumb }"
		/>

		<input
			v-for="(item, index) in searchFilters.fields.filter(
				(e) => e.type === 'Hidden'
			)"
			:key="`hidden-${index}`"
			type="hidden"
			:name="item.name"
			:value="item.value"
		/>

		<div class="bg-primary pb-xl px-layout-margin">
			<div
				:class="[
					'flex flex-col gap-x-0 gap-y-sm mb-lg',
					'>=1024:flex-row >=1024:gap-x-layout-gutter gap-y-0',
				]"
			>
				<BaseInputDropdown
					class="w-full min-w-fit >=1024:w-4col flex-grow"
					:value="computedFilterValues.category"
					:config="computedFilters.category"
					:aria-label="computedFilters.category.placeholder"
					@input="(event) => updateValue('category', event)"
				/>
				<BaseInputText
					v-model="textValue"
					class="w-full >=1024:w-8col flex-grow"
					:placeholder="computedFilters.text.placeholder"
					@keypress.enter="() => updateValue('text', textValue)"
				>
					<template #button>
						<button
							:class="[
								'absolute top-1/2 right-8',
								'transform -translate-y-1/2',
								'rounded-full bg-extra w-40 h-40',
								'flex justify-center items-center',
							]"
							aria-label="Søg efter jobs"
							@click="() => updateValue('text', textValue)"
						>
							<SvgSearch class="w-16 h-16 text-white" />
						</button>
					</template>
				</BaseInputText>
			</div>

			<LimboSearch
				v-slot="{ data, pagination, state, action, misc }"
				:search-filters="filters"
				:config="{
					enableLiveSearch: true,
					allowSearchFiltersMutation: true,
					searchResponseTransformerMethod,
				}"
				class="relative"
			>
				<Transition
					:duration="200"
					mode="out-in"
					name="t-job-custom__content"
				>
					<div v-if="state.isLoading" key="job-list-custom__loader">
						<SearchLoader class="mx-auto w-md text-extra" />
					</div>

					<div v-if="!state.isLoading" key="job-list-custom__content">
						<!-- No results -->
						<div
							v-if="pagination && pagination.total === 0"
							class="text-center w-full col-start-1 col-end-5"
							v-text="'Ingen resultater fundet'"
						></div>

						<div v-if="data && data.length > 0">
							<!-- Items -->
							<div
								:class="[
									'gap-x-layout-gutter gap-y-lg mx-auto w-full >=1024:w-8col ',
								]"
							>
								<div
									v-if="
										jobAgentTile && jobAgentTile.showAgent
									"
									:class="[
										'bg-primary-button rounded-lg mb-sm',
										'p-md text-white',
										'flex flex-col justify-between',
									]"
								>
									<BaseH3
										tag="div"
										v-text="jobAgentTile.title"
									/>

									<div
										:class="
											[
												jobAgentTile.title && 'mt-xs',
												'whitespace-pre-wrap',
											].filter(Boolean)
										"
										v-text="jobAgentTile.text"
									></div>

									<div class="mt-auto">
										<NuxtLinkExt
											v-if="jobAgentTile.link"
											:to="jobAgentTile.link.url"
											:target="jobAgentTile.link.target"
											class="inline-block mt-md"
										>
											<BaseButton
												tag="div"
												:class="['w-fit']"
												outline="white-1"
											>
												<template #default>
													<span
														v-if="jobAgentTile.link"
														v-text="
															jobAgentTile.link
																.name
														"
													></span>
												</template>

												<template #icon>
													<SvgArrow
														class="
															transform
															-rotate-45
														"
													/>
												</template>
											</BaseButton>
										</NuxtLinkExt>
									</div>
								</div>
								<div
									v-for="(item, index) in misc"
									:key="index"
									:class="[
										'bg-sand rounded-lg mb-sm',
										'p-md',
										'flex flex-col justify-between',
									]"
								>
									<BaseH3 tag="div" v-text="item.title" />
									<BaseH3 tag="div" v-text="activeCategory" />

									<div
										v-if="item.text"
										class="mt-xs whitespace-pre-wrap"
										v-text="item.text"
									></div>
									<div class="mt-auto">
										<NuxtLinkExt
											v-if="item.link"
											:to="item.link.url"
											:target="item.link.target"
											class="inline-block mt-md"
										>
											<BaseButton
												tag="div"
												type="sand"
												:class="['w-fit ']"
												outline="black-1"
											>
												<template #default>
													<span
														v-if="item.link"
														v-text="item.link.name"
													></span>
												</template>

												<template #icon>
													<SvgArrow
														class="
															transform
															-rotate-45
														"
													/>
												</template>
											</BaseButton>
										</NuxtLinkExt>
									</div>
								</div>

								<div
									v-for="(item, index) in data"
									:key="`job-${index}`"
								>
									<ContentCard
										:to="item.url"
										:target="item.target"
										:name="item.title"
										light
										class="w-full h-full mb-sm"
									>
										<template #before>
											<span
												v-text="
													item.categories.join(', ')
												"
											></span>
										</template>

										<template #after>
											<div
												class="font-semibold"
												v-text="'Ansøgningsfrist'"
											></div>
											<div
												v-text="
													item.applicationDeadlineText ||
													formatDate(
														item.applicationDeadline
													)
												"
											></div>
										</template>
									</ContentCard>
								</div>
							</div>

							<!-- See more -->
							<div
								v-if="
									pagination.total >
									pagination.limit + pagination.offset
								"
								class="
									w-full
									flex
									justify-center
									mt-78
									>=1024:mt-104
								"
							>
								<button
									:class="[
										'flex items-center space-x-12 mx-auto',
										'uppercase text-button font-semibold',
										'w-fit text-text hover:text-primary-button',
										'group',
									]"
									@click="action.fetchMore"
								>
									<span
										:class="[
											'flex-shrink-0',
											'duration-200 ease-smooth-out',
										]"
										v-text="'Vis flere'"
									></span>

									<SvgCaret
										:class="[
											'w-12 transform rotate-90',
											'duration-200 ease-smooth-out',
											'group-hover:translate-y-2',
										]"
									/>
								</button>
							</div>
						</div>
					</div>
				</Transition>
			</LimboSearch>
		</div>

		<FeedbackForm v-if="activateFeedback" class="mb-lg" />
	</article>
</template>

<script>
import SvgArrow from '~/assets/svgs/arrow-icon.svg?inline';
import SvgCaret from '~/assets/svgs/caret-icon.svg?inline';
import SvgSearch from '~/assets/svgs/search-icon.svg?inline';
import LimboSearch from '~/citi-baseline/components/LimboSearch';
import PageHero from '~/components/main/PageHero';
import ContentCard from '~/components/shared/ContentCard';
import SearchLoader from '~/components/shared/SearchLoader';
import FeedbackForm from '~/components/shared/FeedbackForm';

export default {
	name: 'MainJobListCustom',

	components: {
		SvgArrow,
		SvgCaret,
		SvgSearch,
		LimboSearch,
		PageHero,
		ContentCard,
		SearchLoader,
		FeedbackForm,
	},

	inheritAttrs: false,

	props: {
		header: {
			type: Object,
			required: true,
		},
		breadcrumb: {
			type: Array,
			required: false,
		},
		searchFilters: {
			type: Object,
			required: true,
		},
		jobAgentTile: {
			type: Object,
			required: false,
		},
		activateFeedback: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			filters: this.searchFilters,
			textValue:
				this.searchFilters?.fields?.find((e) => e.name === 'text')
					.value || '',
		};
	},

	computed: {
		computedFilters() {
			const response = {};
			this.searchFilters?.fields?.forEach((element) => {
				response[element.name] = element;
			});
			return response;
		},
		computedFilterValues() {
			const response = {};
			this.searchFilters?.fields?.forEach((element) => {
				response[element.name] = Array.isArray(element.value)
					? element.value.find((e) => e.checked)?.value
					: element.value;
			});
			return response;
		},
		activeCategory() {
			console.log(this.filters.fields);
			return this.filters.fields
				.find((e) => e.name === 'category')
				.value.find((e) => e.checked === true).label;
		},
	},

	watch: {
		'computedFilterValues.text'(value) {
			this.textValue = value;
		},
	},

	methods: {
		updateValue(name, event) {
			const field = this.filters.fields.find((e) => e.name === name);
			if (typeof event === 'string' && Array.isArray(field.value)) {
				field.value.forEach((element) => {
					element.checked = element.value === event;
				});
			} else if (typeof event === 'string') {
				field.value = event;
			} else {
				field.value = event?.target?.value ?? '';
			}
		},

		formatDate(dateString) {
			const date = new Date(dateString);
			const day = `0${date.getDate()}`.slice(-2);
			const month = `0${date.getMonth() + 1}`.slice(-2);
			const year = date.getFullYear();

			return `${day}.${month}.${year}`;
		},
		searchResponseTransformerMethod(value) {
			if (value.data?.infoBoxes) {
				value.data.misc = value.data.infoBoxes;
				delete value.data.infoBoxes;
			}
			return value;
		},
	},
};
</script>

<style lang="postcss">
.t-job-custom__content-enter-active,
.t-job-custom__content-leave-active {
	@apply duration-200 ease-smooth-out;
}

.t-job-custom__content-enter,
.t-job-custom__content-leave-to {
	@apply opacity-0;
}
</style>
